/**
 * FAQ Component Constants
 * This file contains all string constants used across FAQ components,
 * organized by component for easy maintenance and localization.
 */

// ============================================================================
// FaqContentEditor Component Constants
// ============================================================================
export const FAQ_CONTENT_EDITOR_LABELS = {
  PAGE_HEADER: 'FAQ Documentation Manager',
  ADD_FAQ_BUTTON: 'Add FAQ Entry',
  TITLE: 'FAQ Documentation Manager',
};

// ============================================================================
// FaqForm Component Constants
// ============================================================================
export const FAQ_FORM_LABELS = {
  // Page Headers
  PAGE_HEADER_CREATE: 'Question Add Details',
  PAGE_HEADER_EDIT: 'Question Edit Details',
  
  // Form Instructions
  FORM_SUBTITLE: 'Use this form to edit, review and publish FAQs to the Frequently asked Questions pop out tab.',
  
  // Field Labels
  FIELD_LABEL_LAST_UPDATED: 'Last Updated',
  FIELD_LABEL_PUBLISHED: 'Published',
  FIELD_LABEL_QUESTION_NAME: 'Question Name',
  FIELD_LABEL_CATEGORY: 'Category',
  FIELD_LABEL_ANSWER_DESCRIPTION: 'Answer Description',
  FIELD_LABEL_MORE_INFO_DESCRIPTION: 'More Info Description',
  FIELD_LABEL_NOTES: 'Notes',
  FIELD_LABEL_METRIC_AVAILABLE_IN: 'Metric Available In',
  
  // Field Values
  FIELD_VALUE_NOT_AVAILABLE: '--',
  FIELD_VALUE_YES: 'Yes',
  FIELD_VALUE_NO: 'No',
  
  // Dropdown Placeholders
  DROPDOWN_CATEGORY_PLACEHOLDER: 'Select a category',
  DROPDOWN_METRIC_PLACEHOLDER: 'Select metric available in',
  
  // Validation Error Messages
  ERROR_QUESTION_REQUIRED: 'Question Name is required',
  ERROR_ANSWER_REQUIRED: 'Answer is required',
  ERROR_CATEGORY_REQUIRED: 'Category is required',
  ERROR_MORE_INFO_REQUIRED: 'More Info is required',
  ERROR_NOTES_REQUIRED: 'Notes are required',
  ERROR_METRIC_REQUIRED: 'At least one metric is required',
  ERROR_FETCHING_CATEGORIES: 'Error fetching categories:',
  ERROR_SAVING_FAQ: 'Error saving FAQ. Please try again.',
  
  // Success Messages
  SUCCESS_FAQ_SAVED: 'Faq saved successfully!',
  SUCCESS_FAQ_DELETED: 'FAQ deleted successfully!',
  
  // Deletion Error Message
  ERROR_DELETING_FAQ: 'Error deleting FAQ. Please try again.',
};

// ============================================================================
// FaqActions Component Constants
// ============================================================================
export const FAQ_ACTIONS_LABELS = {
  BUTTON_CANCEL: 'Cancel',
  BUTTON_SAVE: 'Save',
  BUTTON_SAVING: 'Saving...',
  BUTTON_DELETE: 'Delete',
  BUTTON_DELETING: 'Deleting...',
};

// ============================================================================
// FaqList Component Constants
// ============================================================================
export const FAQ_LIST_LABELS = {
  // Page Title
  PAGE_TITLE: 'FAQ Documentation Management',
  
  // Table Headers
  HEADER_QUESTION: 'Question Name',
  HEADER_CATEGORY: 'Category',
  HEADER_LAST_UPDATED: 'Last Updated',
  HEADER_LAST_EDITED_BY: 'Last Edited By',
  HEADER_PUBLISHED: 'Published',
  
  // Search
  SEARCH_PLACEHOLDER: 'Search by Question',
  
  // Field Values
  FIELD_VALUE_NOT_AVAILABLE: 'N/A',
  FIELD_VALUE_YES: 'Yes',
  FIELD_VALUE_NO: 'No',
  FIELD_VALUE_DASH: '-',

  SHOWING: 'Showing',
  RECORDS: 'records',
  EXPORT_DATA: 'Export Data',
  EXPORT_BUTTON: 'Export Data',
  EXPORTING: 'Exporting...',
  SEARCH_QUESTIONNAME_PLACEHOLDER: 'Search by Question Name',
  NO_DATA_FOUND: 'No FAQ entries found.',
};

// ============================================================================
// MultiSelectDropdown Component Constants
// ============================================================================
export const MULTI_SELECT_DROPDOWN_LABELS = {
  PLACEHOLDER_DEFAULT: 'Select options',
  SELECTED_SINGLE: '{0}', // Placeholder for single selection display
  SELECTED_MULTIPLE: '{0} selected', // Placeholder for multiple selections count
  ARROW_DOWN: '▼',
};

// ============================================================================
// Modal/Popup Constants (used in faq-function.ts)
// ============================================================================
export const FAQ_MODAL_LABELS = {
  // Unpublish Modal
  UNPUBLISH_TITLE: 'You have changed a published FAQ content, saving will unpublish the existing content.',
  UNPUBLISH_MESSAGE_PREFIX: 'Click "',
  UNPUBLISH_MESSAGE_ACTION: 'Unpublish',
  UNPUBLISH_MESSAGE_SUFFIX: '" to update the Frequently Asked Questions to pop out tab. Click "Do Not Publish" to return to the edit screen.',
  UNPUBLISH_CANCEL_TEXT: 'Do Not Publish',
  UNPUBLISH_CONFIRM_TEXT: 'Save and Unpublish',
  
  // Publish Modal
  PUBLISH_TITLE: 'You are about to publish a FAQ to the Frequently Asked Questions pop out tab.',
  PUBLISH_MESSAGE_PREFIX: 'Click "',
  PUBLISH_MESSAGE_ACTION: 'Save and Publish',
  PUBLISH_MESSAGE_SUFFIX: '" to update the Frequently Asked Questions to pop out tab. Click "Do Not Publish" to return to the edit screen.',
  PUBLISH_CANCEL_TEXT: 'Do Not Publish',
  PUBLISH_CONFIRM_TEXT: 'Save and Publish',
  
  // Modal Types
  MODAL_TYPE_UNPUBLISH: 'modalPopupUnpublish',
  MODAL_TYPE_PUBLISH: 'modalPopupPublish',
  
  // Common Action Text
  ACTION_UNPUBLISH: 'Unpublish',
  ACTION_PUBLISH: 'Publish',
  ACTION_SAVE_AND_PREFIX: 'Save and ',
};

// ============================================================================
// Table Configuration Constants
// ============================================================================
export const FAQ_LIST_TABLE_CONFIG = {
  HEADERS: [
    { name: 'Question Name', searchField: true, sorting: true, enableSorting: true, order: 'ASC' as const, width: '400px' },
    { name: 'Category', searchField: false, sorting: true, enableSorting: true, order: 'ASC' as const, width: '230px' },
    { name: 'Last Updated', searchField: false, sorting: false, enableSorting: false, order: 'DSC' as const, width: '155px' },
    { name: 'Last Edited By', searchField: false, sorting: false, enableSorting: false, order: 'DSC' as const, width: '150px' }
  ],
  PAGINATION_CONFIG: [
    { value: 25, label: 25 },
    { value: 50, label: 50 },
    { value: 100, label: 100 },
  ],
  DEFAULT_PAGE_SIZE: 25,
};

// ============================================================================
// Common Constants
// ============================================================================
export const FAQ_COMMON_CONSTANTS = {
  REQUIRED_INDICATOR: '*',
  EMPTY_RICH_TEXT: '<p></p>',
  ERROR_MORE_INFO_INVALID_URL: 'More Info must be a valid URL',
  LINK_TRUE: 'true',
  LINK_FALSE: 'false',
  SORT_ORDER_ASC: 'ASC' as const,
  SORT_ORDER_DSC: 'DSC' as const,
};
